import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronRight, MapPin } from 'lucide-react'
import type { Region, RegionType, District, SettlementType } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { RegionCardSkeleton } from '@/components/region/RegionCardSkeleton'
import { useDistrictsByNation } from '@/hooks/useDistrictsByNation'

interface NationRegionsListProps {
  regions: Region[]
  isLoading: boolean
  error: Error | null
}

const regionTypeLabels: Record<RegionType, string> = {
  coastal: 'Coastal',
  mountain: 'Mountain',
  plains: 'Plains',
  forest: 'Forest',
  urban_core: 'Urban Core',
}

const settlementLabels: Record<SettlementType, string> = {
  rural: 'Rural',
  suburban: 'Suburban',
  urban: 'Urban',
}

function formatPopulation(population: number): string {
  if (population >= 1000000) {
    return `${(population / 1000000).toFixed(1)}M`
  }
  if (population >= 1000) {
    return `${(population / 1000).toFixed(0)}K`
  }
  return population.toLocaleString()
}

interface RegionWithDistrictsCardProps {
  region: Region
  districts: District[]
}

function RegionWithDistrictsCard({ region, districts }: RegionWithDistrictsCardProps) {
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)

  const regionDistricts = districts.filter((d) => d.region_id === region.id)
  const districtCount = regionDistricts.length

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <MapPin className="text-muted-foreground h-4 w-4" />
            <CardTitle className="text-lg">{region.name}</CardTitle>
          </div>
          <Badge variant="secondary">{regionTypeLabels[region.region_type]}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Region Stats */}
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Seats:</span>{' '}
              <span className="font-medium">{region.seat_count}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Pop:</span>{' '}
              <span className="font-medium">{formatPopulation(region.population)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Districts:</span>{' '}
              <span className="font-medium">{districtCount}</span>
            </div>
          </div>

          {/* Expandable Districts Section */}
          {districtCount > 0 && (
            <div className="border-t pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-between px-0"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <span className="text-muted-foreground text-xs">
                  {isExpanded ? 'Hide' : 'Show'} districts
                </span>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>

              {isExpanded && (
                <div className="mt-2 space-y-2">
                  {regionDistricts.map((district) => (
                    <div
                      key={district.id}
                      className="hover:bg-muted/50 flex cursor-pointer items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors"
                      onClick={() => navigate(`/districts/${district.id}`)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{district.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {settlementLabels[district.settlement_type]}
                        </Badge>
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {formatPopulation(district.population)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* View Region Link */}
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => navigate(`/regions/${region.id}`)}
          >
            View Region Details
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export function NationRegionsList({ regions, isLoading, error }: NationRegionsListProps) {
  // Get all districts for the nation to count per region
  const nationId = regions[0]?.nation_id
  const { districts = [] } = useDistrictsByNation(nationId)

  const totalSeats = regions.reduce((sum, r) => sum + r.seat_count, 0)
  const totalPopulation = regions.reduce((sum, r) => sum + r.population, 0)

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold">Regions & Districts</h2>
          <Badge variant="outline">{regions.length} regions</Badge>
        </div>
        <div className="text-muted-foreground text-sm">
          {totalSeats} total seats | {formatPopulation(totalPopulation)} population
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          Failed to load regions. Please try again later.
        </div>
      )}

      {isLoading && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <RegionCardSkeleton key={i} />
          ))}
        </div>
      )}

      {!isLoading && !error && regions.length === 0 && (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <h3 className="text-lg font-medium">No regions yet</h3>
          <p className="text-muted-foreground mt-1">
            This nation doesn't have any regions configured yet.
          </p>
        </div>
      )}

      {!isLoading && !error && regions.length > 0 && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {regions.map((region) => (
            <RegionWithDistrictsCard
              key={region.id}
              region={region}
              districts={districts}
            />
          ))}
        </div>
      )}
    </div>
  )
}
